// src/components/PriceDisplay.tsx
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useCurrency, CURRENCY_FLAGS, CURRENCY_SYMBOLS, Currency } from '../hooks/useCurrency';

interface PriceDisplayProps {
    priceInTJS: number;
    size?: 'small' | 'medium' | 'large' | number | string;
    buttonSize?: 'small' | 'medium' | 'large' | number | string;
    showButton?: boolean;
    showButtonFlag?: boolean;
}

const ALL_CURRENCIES: Currency[] = ['TJS', 'RUB', 'USD', 'EUR'];

// Предустановленные размеры для цены
const PRESET_SIZES: Record<string, number> = {
    small: 20,
    medium: 28,
    large: 36,
};

// Предустановленные размеры для кнопки
const PRESET_BUTTON_SIZES: Record<string, { padding: string; fontSize: number; flagSize: number; gap: number }> = {
    small: {
        padding: '4px 8px',
        fontSize: 11,
        flagSize: 14,
        gap: 3,
    },
    medium: {
        padding: '6px 12px',
        fontSize: 13,
        flagSize: 16,
        gap: 5,
    },
    large: {
        padding: '8px 16px',
        fontSize: 15,
        flagSize: 18,
        gap: 6,
    },
};

export const PriceDisplay: React.FC<PriceDisplayProps> = ({
                                                              priceInTJS,
                                                              size = 'medium',
                                                              buttonSize = 'medium',
                                                              showButton = true,
                                                              showButtonFlag = true,
                                                          }) => {
    const { currency, setCurrency, formatPrice, convert, updatedAt } = useCurrency();
    const [open, setOpen] = useState(false);
    const buttonRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, direction: 'down' });
    const [ratesAge, setRatesAge] = useState<number | null>(null);

    // Получаем размер цены в пикселях
    const getFontSize = (): number => {
        if (typeof size === 'number') return size;
        if (typeof size === 'string') {
            if (size.endsWith('px')) return parseInt(size, 10);
            if (!isNaN(Number(size))) return Number(size);
            return PRESET_SIZES[size] || 28;
        }
        return 28;
    };

    // Получаем стили кнопки
    const getButtonStyles = () => {
        if (typeof buttonSize === 'number') {
            return {
                padding: `${Math.max(4, buttonSize * 0.25)}px ${Math.max(8, buttonSize * 0.5)}px`,
                fontSize: Math.max(10, buttonSize * 0.45),
                flagSize: Math.max(12, buttonSize * 0.6),
                gap: Math.max(3, buttonSize * 0.2),
            };
        }

        if (typeof buttonSize === 'string') {
            if (buttonSize.endsWith('px')) {
                const px = parseInt(buttonSize, 10);
                return {
                    padding: `${Math.max(4, px * 0.25)}px ${Math.max(8, px * 0.5)}px`,
                    fontSize: Math.max(10, px * 0.45),
                    flagSize: Math.max(12, px * 0.6),
                    gap: Math.max(3, px * 0.2),
                };
            }
            if (!isNaN(Number(buttonSize))) {
                const px = Number(buttonSize);
                return {
                    padding: `${Math.max(4, px * 0.25)}px ${Math.max(8, px * 0.5)}px`,
                    fontSize: Math.max(10, px * 0.45),
                    flagSize: Math.max(12, px * 0.6),
                    gap: Math.max(3, px * 0.2),
                };
            }
            return PRESET_BUTTON_SIZES[buttonSize] || PRESET_BUTTON_SIZES.medium;
        }

        return PRESET_BUTTON_SIZES.medium;
    };

    const fontSize = getFontSize();
    const buttonStyles = getButtonStyles();

    const formatOther = (c: Currency): string => {
        const val = convert(priceInTJS, c);
        switch (c) {
            case 'TJS': return Math.round(val).toLocaleString('ru') + '\u00A0TJS';
            case 'RUB': return '₽\u00A0' + Math.round(val).toLocaleString('ru');
            case 'USD': return '$\u00A0' + val.toFixed(2);
            case 'EUR': return '€\u00A0' + val.toFixed(2);
            default: return String(val);
        }
    };

    useEffect(() => {
        if (updatedAt) {
            const hours = (Date.now() - updatedAt.getTime()) / (1000 * 60 * 60);
            setRatesAge(hours);
        }
    }, [updatedAt]);

    // Обновляем позицию dropdown при открытии с учётом границ экрана
    useEffect(() => {
        if (open && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const dropdownWidth = 240;
            const dropdownHeight = 380;
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const scrollX = window.scrollX;
            const scrollY = window.scrollY;

            const spaceBelow = viewportHeight - rect.bottom;
            let top: number;
            let direction: 'up' | 'down';

            if (spaceBelow < dropdownHeight && rect.top > dropdownHeight) {
                top = rect.top + scrollY - dropdownHeight - 8;
                direction = 'up';
            } else {
                top = rect.bottom + scrollY + 8;
                direction = 'down';
            }

            let left = rect.right + scrollX - dropdownWidth;

            if (left < scrollX + 8) {
                left = scrollX + 8;
            }

            if (left + dropdownWidth > viewportWidth + scrollX - 8) {
                left = viewportWidth + scrollX - dropdownWidth - 8;
            }

            setDropdownPosition({ top, left, direction });
        }
    }, [open]);

    // Закрытие при клике вне (без скролла!)
    useEffect(() => {
        if (!open) return;

        const handleClickOutside = (e: MouseEvent) => {
            if (buttonRef.current && !buttonRef.current.contains(e.target as Node)) {
                if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                    setOpen(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open]);

    const getDateDisplay = () => {
        if (!updatedAt) return null;

        const now = new Date();
        const updateDate = new Date(updatedAt);
        const isToday = updateDate.toDateString() === now.toDateString();
        const isYesterday = updateDate.toDateString() === new Date(now.setDate(now.getDate() - 1)).toDateString();

        if (isToday) return "сегодня";
        if (isYesterday) return "вчера";
        return updateDate.toLocaleDateString('ru', { day: 'numeric', month: 'long' });
    };

    return (
        <div
            ref={buttonRef}
            style={{ display: 'inline-block' }}
            onClick={(e) => e.stopPropagation()}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: Math.max(6, fontSize * 0.3), flexWrap: 'wrap' }}>
                <span style={{
                    fontSize: fontSize,
                    fontWeight: 700,
                    color: '#1f2937',
                    letterSpacing: '-0.5px',
                    lineHeight: 1.2,
                }}>
                    {formatPrice(priceInTJS)}
                </span>

                {showButton && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpen(v => !v);
                        }}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: buttonStyles.gap,
                            padding: buttonStyles.padding,
                            borderRadius: 30,
                            border: '1.5px solid #e5e7eb',
                            background: open ? '#059669' : 'white',
                            color: open ? 'white' : '#374151',
                            fontSize: buttonStyles.fontSize,
                            fontWeight: 500,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            userSelect: 'none',
                        }}
                    >
                        {showButtonFlag && (
                            <span style={{ fontSize: buttonStyles.flagSize }}>{CURRENCY_FLAGS[currency]}</span>
                        )}
                        <span>{CURRENCY_SYMBOLS[currency]}</span>
                        <span style={{
                            fontSize: buttonStyles.fontSize * 0.8,
                            opacity: 0.7,
                            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease',
                            display: 'inline-block',
                        }}>▾</span>
                    </button>
                )}
            </div>

            {open && createPortal(
                <div
                    ref={dropdownRef}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        position: 'absolute',
                        top: dropdownPosition.top,
                        left: dropdownPosition.left,
                        zIndex: 9999,
                        background: 'white',
                        borderRadius: 16,
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        overflow: 'hidden',
                        minWidth: 220,
                        maxWidth: `min(280px, calc(100vw - 32px))`,
                        animation: `fadeSlideIn 0.15s ease`,
                        transformOrigin: dropdownPosition.direction === 'up' ? 'bottom' : 'top',
                    }}
                >
                    <div style={{
                        padding: '12px 16px 8px',
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#9ca3af',
                        textTransform: 'uppercase',
                        borderBottom: '1px solid #f3f4f6',
                    }}>
                        Выберите валюту
                    </div>

                    <div style={{
                        maxHeight: 400,
                        overflowY: 'auto',
                        WebkitOverflowScrolling: 'touch',
                    }}>
                        {ALL_CURRENCIES.map((c, i) => {
                            const isSelected = c === currency;
                            const convertedPrice = formatOther(c);

                            return (
                                <button
                                    key={c}
                                    onClick={() => { setCurrency(c); setOpen(false); }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '100%',
                                        padding: '12px 16px',
                                        border: 'none',
                                        background: isSelected ? '#f0fdf4' : 'white',
                                        cursor: 'pointer',
                                        transition: 'background 0.15s',
                                        borderBottom: i < ALL_CURRENCIES.length - 1 ? '1px solid #f9fafb' : 'none',
                                        gap: 12,
                                    }}
                                >
                                    <span style={{ fontSize: 22 }}>{CURRENCY_FLAGS[c]}</span>
                                    <div style={{ flex: 1, textAlign: 'left' }}>
                                        <div style={{
                                            fontSize: 14,
                                            fontWeight: isSelected ? 600 : 400,
                                            color: isSelected ? '#059669' : '#1f2937',
                                        }}>
                                            {CURRENCY_SYMBOLS[c]}
                                        </div>
                                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>
                                            {c === 'TJS' ? 'Сомони' : c === 'RUB' ? 'Рубль' : c === 'USD' ? 'Доллар' : 'Евро'}
                                        </div>
                                    </div>
                                    <div style={{
                                        fontSize: 14,
                                        fontWeight: 600,
                                        color: isSelected ? '#059669' : '#374151',
                                    }}>
                                        {convertedPrice}
                                    </div>
                                    {isSelected && (
                                        <div style={{
                                            width: 20, height: 20,
                                            borderRadius: '50%',
                                            background: '#059669',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}>
                                            <span style={{ color: 'white', fontSize: 11, fontWeight: 700 }}>✓</span>
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {updatedAt && (
                        <div style={{
                            padding: '8px 16px',
                            fontSize: 11,
                            color: '#9ca3af',
                            background: '#fafafa',
                            borderTop: '1px solid #f3f4f6',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                🕐 Курс от {getDateDisplay()}
                            </div>
                            {ratesAge && ratesAge > 24 ? (
                                <div style={{ color: '#f59e0b', fontSize: 11 }}>
                                    ⚠️ Курс устарел более чем на 24 часа
                                </div>
                            ) : ratesAge && ratesAge > 12 ? (
                                <div style={{ color: '#9ca3af', fontSize: 11 }}>
                                    🕐 Курс обновлён {Math.floor(ratesAge)} часов назад
                                </div>
                            ) : null}
                        </div>
                    )}
                </div>,
                document.body
            )}

            <style>{`
                @keyframes fadeSlideIn {
                    from { 
                        opacity: 0; 
                        transform: ${dropdownPosition.direction === 'up' ? 'translateY(6px)' : 'translateY(-6px)'};
                    }
                    to { 
                        opacity: 1; 
                        transform: translateY(0);
                    }
                }
            `}</style>
        </div>
    );
};